import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ShareNetwork, ArrowClockwise, Trash, Lock, CheckCircle, Warning } from '@phosphor-icons/react';

const AUTO_INTERVAL_MS = 15 * 60 * 1000; // 15 min

export default function SheetSyncPage() {
  const { user } = useAuth();
  const isPremium = user?.role === 'admin' || user?.role === 'manager';

  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetName, setSheetName] = useState('My Leads Sheet');
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState(null);
  const [autoOn, setAutoOn] = useState(false);
  const timerRef = useRef(null);

  const load = () => {
    api.get('/sheet-sync').then(({ data }) => setSubs(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isPremium) load();
    else setLoading(false);
  }, [isPremium]);

  // Auto-pull every 15 min when toggled on
  useEffect(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoOn && subs.length > 0) {
      timerRef.current = setInterval(() => {
        subs.forEach(s => runSync(s.sub_id, true));
      }, AUTO_INTERVAL_MS);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoOn, subs.length]); // eslint-disable-line

  const subscribe = async (e) => {
    e.preventDefault();
    if (!sheetUrl.trim()) return;
    setAdding(true);
    setMsg(null);
    try {
      await api.post('/sheet-sync/subscribe', {
        sheet_url: sheetUrl.trim(),
        name: sheetName.trim() || 'My Sheet',
        // Smart default mapping — matches common sheet headers case-insensitively on backend later
        mappings: {
          'First Name': 'first_name', 'first_name': 'first_name', 'FirstName': 'first_name',
          'Last Name': 'last_name', 'last_name': 'last_name', 'LastName': 'last_name',
          'Name': 'first_name', 'Full Name': 'first_name',
          'Email': 'email', 'email': 'email', 'E-mail': 'email',
          'Phone': 'phone', 'phone': 'phone', 'Mobile': 'mobile',
          'Company': 'company', 'company': 'company', 'Organization': 'company',
          'Title': 'title', 'Job Title': 'title',
          'City': 'address_city', 'State': 'address_state', 'Zip': 'address_zip',
          'Country': 'address_country', 'Address': 'address_street',
        },
        dedupe_field: 'email',
      });
      setSheetUrl('');
      setSheetName('My Leads Sheet');
      setMsg({ kind: 'ok', text: 'Sheet linked. Hit "Pull Now" to import.' });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setMsg({ kind: 'err', text: typeof d === 'string' ? d : 'Could not link sheet.' });
    } finally {
      setAdding(false);
    }
  };

  const runSync = async (subId, silent = false) => {
    if (!silent) setMsg(null);
    try {
      const { data } = await api.post(`/sheet-sync/${subId}/run`);
      if (!silent) setMsg({ kind: 'ok', text: `Imported ${data.created} new · Skipped ${data.skipped} duplicates (out of ${data.total_rows}).` });
      load();
    } catch (err) {
      if (!silent) {
        const d = err.response?.data?.detail;
        setMsg({ kind: 'err', text: typeof d === 'string' ? d : 'Sync failed.' });
      }
    }
  };

  const removeSub = async (subId) => {
    if (!window.confirm('Unlink this sheet? (Imported contacts stay.)')) return;
    await api.delete(`/sheet-sync/${subId}`);
    load();
  };

  if (!isPremium) {
    return (
      <div className="flex flex-col h-full" data-testid="sheet-sync-page">
        <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D0D0D0] border-b border-[#999] px-3 py-1.5 flex items-center gap-2 shrink-0">
          <ShareNetwork size={16} weight="bold" className="text-[#0B5394]" />
          <span className="text-[13px] font-bold text-[#333]">Google Sheet Sync</span>
          <span className="text-[10px] px-1.5 py-0.5 bg-[#F39C12] text-white font-bold rounded-sm ml-1">PREMIUM</span>
        </div>
        <div className="flex-1 flex items-center justify-center bg-[#E8E8E8] p-6">
          <div className="max-w-md bg-white border border-[#999] p-6 text-center" data-testid="sheet-sync-locked">
            <Lock size={32} weight="duotone" className="text-[#F39C12] mx-auto mb-3" />
            <h2 className="text-[15px] font-bold text-[#333] mb-1">Premium Feature</h2>
            <p className="text-[12px] text-[#666] mb-4">
              Auto-sync contacts from any public Google Sheet. Perfect for marketing teams and shared lead lists.
            </p>
            <ul className="text-[11px] text-left text-[#555] space-y-1 mb-4 list-disc pl-5">
              <li>Paste any "Anyone with the link" Google Sheet URL</li>
              <li>New rows automatically become CRM contacts</li>
              <li>Email-based deduplication (no duplicates)</li>
              <li>Manual "Pull Now" + optional 15-min auto-refresh</li>
            </ul>
            <p className="text-[11px] text-[#999]">Contact your admin to enable Premium on your account.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="sheet-sync-page">
      <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D0D0D0] border-b border-[#999] px-3 py-1.5 flex items-center gap-2 shrink-0">
        <ShareNetwork size={16} weight="bold" className="text-[#0B5394]" />
        <span className="text-[13px] font-bold text-[#333]">Google Sheet Sync</span>
        <span className="text-[10px] px-1.5 py-0.5 bg-[#F39C12] text-white font-bold rounded-sm ml-1">PREMIUM</span>
        <div className="flex-1" />
        {subs.length > 0 && (
          <label className="flex items-center gap-1.5 text-[11px] text-[#333] cursor-pointer" data-testid="auto-pull-toggle">
            <input
              type="checkbox"
              checked={autoOn}
              onChange={(e) => setAutoOn(e.target.checked)}
              className="w-3 h-3"
            />
            Auto-pull every 15 min
          </label>
        )}
      </div>

      <div className="flex-1 overflow-auto p-2 sm:p-3 bg-[#E8E8E8]">
        {/* Add new sheet */}
        <div className="bg-[#F5F5F0] border border-[#999] mb-3">
          <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
            <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">Link a New Sheet</h3>
          </div>
          <form onSubmit={subscribe} className="p-3 space-y-2" data-testid="add-sheet-form">
            <p className="text-[11px] text-[#666] mb-1">
              In Google Sheets → Share → change access to <b>"Anyone with the link — Viewer"</b>, then paste the URL below.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="url"
                required
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="flex-1 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                data-testid="sheet-url-input"
              />
              <input
                value={sheetName}
                onChange={(e) => setSheetName(e.target.value)}
                placeholder="Nickname"
                className="sm:w-40 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                data-testid="sheet-name-input"
              />
              <Button type="submit" disabled={adding} className="h-[28px] px-3 text-[12px] rounded-sm bg-[#0B5394] hover:bg-[#073763]" data-testid="link-sheet-btn">
                {adding ? 'Linking...' : 'Link Sheet'}
              </Button>
            </div>
            {msg && (
              <div
                className={`flex items-start gap-1.5 p-2 text-[11px] border ${
                  msg.kind === 'ok' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
                }`}
                data-testid="sheet-sync-msg"
              >
                {msg.kind === 'ok' ? <CheckCircle size={13} weight="bold" /> : <Warning size={13} weight="bold" />}
                {msg.text}
              </div>
            )}
          </form>
        </div>

        {/* Linked sheets list */}
        <div className="bg-[#F5F5F0] border border-[#999]">
          <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
            <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">
              Linked Sheets ({subs.length})
            </h3>
          </div>
          {loading ? (
            <div className="p-4 text-center"><div className="w-5 h-5 border-2 border-[#0B5394] border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : subs.length === 0 ? (
            <p className="p-4 text-[12px] text-[#999] text-center">No sheets linked yet.</p>
          ) : (
            <table className="w-full text-[11px]" data-testid="sheets-table">
              <thead>
                <tr className="bg-[#E8E8E8] border-b border-[#999]">
                  <th className="px-3 py-2 text-left font-semibold text-[#333]">Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-[#333] hidden sm:table-cell">Last Sync</th>
                  <th className="px-3 py-2 text-left font-semibold text-[#333] hidden sm:table-cell">Last Imported</th>
                  <th className="px-3 py-2 text-right font-semibold text-[#333]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.sub_id} className="border-b border-[#DDD] hover:bg-[#E8E8E8]" data-testid={`sheet-row-${s.sub_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-[#333]">{s.name}</div>
                      <a href={s.sheet_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#0B5394] hover:underline truncate block max-w-[200px] sm:max-w-[400px]">
                        {s.sheet_url}
                      </a>
                    </td>
                    <td className="px-3 py-2 text-[#666] hidden sm:table-cell">
                      {s.last_synced_at ? new Date(s.last_synced_at).toLocaleString() : <span className="text-[#999]">Never</span>}
                    </td>
                    <td className="px-3 py-2 text-[#666] hidden sm:table-cell">
                      {s.last_synced_at ? `${s.last_synced_count ?? 0} new` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => runSync(s.sub_id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-[#0B5394] hover:bg-[#073763] text-white rounded-sm"
                        data-testid={`run-sync-${s.sub_id}`}
                      >
                        <ArrowClockwise size={11} weight="bold" />
                        Pull Now
                      </button>
                      <button
                        onClick={() => removeSub(s.sub_id)}
                        className="inline-flex items-center px-2 py-1 ml-1 text-[11px] text-[#C0392B] hover:bg-red-50 rounded-sm"
                        data-testid={`del-sheet-${s.sub_id}`}
                      >
                        <Trash size={11} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-[10px] text-[#999] mt-3">
          Fields are auto-matched for common headers: First Name, Last Name, Email, Phone, Company, Title, Address. Duplicates are skipped by email.
        </p>
      </div>
    </div>
  );
}
